<script>
import appConfig from '@/app.config'
import PageHeader from '@/components/page-header'
import { Helper } from '@/mixin/helper'
import NotificationToggle from './NotificationToggle.vue'
import SectionIcon from './SectionIcon.vue'
import McButton from '@/components/McButton'
import * as notificationApi from '@/services/rest-api-sms/lib/notification/notification'

const soundNotification = require('@/assets/sounds/notification_sound_websms.mp3')

export default {
    page: {
        title: 'Notification Settings',
        meta: [{ name: 'description', content: appConfig.description }],
    },
    components: {
        PageHeader,
        McButton,
        NotificationToggle,
        SectionIcon,
    },
    mixins: [Helper],

    data() {
        return {
            notificationTypes: [],
            selectedNotificationTypes: {},
            hasRule: false,
            initialRule: null,
            initialState: null,
            isNotificationEnabled: false,
        }
    },

    computed: {
        title() {
            return 'Pengaturan Notifikasi'
        },
    },

    async mounted() {
        await this.initPage()
        await this.saveInitialState()
    },

    methods: {
        saveInitialState() {
            this.initialState = {
                selectedNotificationTypes: JSON.parse(JSON.stringify(this.selectedNotificationTypes)),
            }
        },

        async initPage() {
            try {
                const responseRule = await notificationApi.getNotificationRule()
                const rule = responseRule?.data || {}

                this.hasRule = Boolean(rule.notification_rule_id)
                this.initialRule = rule

                const notificationTypesFromRule = Array.isArray(rule.notification_types)
                    ? rule.notification_types
                    : []

                const enabledCodes = notificationTypesFromRule.map((t) => t.code)

                const responseType = await notificationApi.getNotificationType()

                // 🔥 KHUSUS TECHNICIAN
                this.isNotificationEnabled = !!rule.is_enabled

                const allTechnicianTypes =
                    responseType.data?.paginated_result.filter((t) => t.related_role === 'technician') || []

                this.notificationTypes = allTechnicianTypes

                this.selectedNotificationTypes = this.notificationTypes.reduce((acc, type) => {
                    acc[type.code] = enabledCodes.includes(type.code)
                    return acc
                }, {})
            } catch (error) {
                console.error(error)
                this.$mcToast.error('Gagal memuat pengaturan notifikasi')
            }
        },

        buildPatchPayload() {
            return {
                is_enabled: this.isNotificationEnabled,
                notification_type_ids: this.mapNotificationTypeIDs(),
            }
        },

        isSameArray(a = [], b = []) {
            if (a.length !== b.length) return false
            return [...a].sort().every((val, i) => val === [...b].sort()[i])
        },

        hasChanges() {
            return !this.isSameState(this.initialState, {
                notificationTypeIds: this.mapNotificationTypeIDs(),
            })
        },

        isSameState(a, b) {
            return this.isSameArray(a.notificationTypeIds || [], b.notificationTypeIds || [])
        },

        buildCreatePayload() {
            return {
                is_enabled: this.isNotificationEnabled, // 🔥 WAJIB
                notification_type_ids: this.mapNotificationTypeIDs(),
            }
        },

        mapNotificationTypeIDs() {
            return this.notificationTypes
                .filter((t) => this.selectedNotificationTypes[t.code])
                .map((t) => t.notification_type_id)
        },

        async saveSettings() {
            try {
                const payload = this.hasRule ? this.buildPatchPayload() : this.buildCreatePayload()

                if (this.hasRule) {
                    await notificationApi.patchNotificationRule(payload)
                } else {
                    await notificationApi.createNotificationRule(payload)
                }

                this.hasRule = true
                this.saveInitialState()

                await this.$store.dispatch('notification/fetchNotificationRule')

                this.$mcToast.success('Pengaturan notifikasi berhasil disimpan')
            } catch (err) {
                this.$mcToast.error('Gagal menyimpan pengaturan')
                console.error(err)
            }
        },

        restoreInitialState() {
            if (!this.initialState) return

            this.selectedNotificationTypes = JSON.parse(
                JSON.stringify(this.initialState.selectedNotificationTypes)
            )
        },

        cancel() {
            if (this.hasChanges()) {
                this.$bvModal
                    .msgBoxConfirm('Perubahan belum disimpan. Yakin ingin membatalkan?', {
                        okVariant: 'danger',
                    })
                    .then((confirmed) => {
                        if (confirmed) this.restoreInitialState()
                    })
            } else {
                this.restoreInitialState()
            }
        },

        toggleNotification(code, value) {
            this.$set(this.selectedNotificationTypes, code, value)
        },

        onPlay() {
            const audio = new Audio(soundNotification)
            audio.play()
        },
    },
}
</script>

<template>
    <div class="mc-notification-setting pb-5">
        <!-- Header -->
        <div class="mc-notification-setting__header mb-4">
            <PageHeader :title="title" />
            <p class="mc-notification-setting__subtitle">
                Konfigurasikan notifikasi mana yang ingin Anda terima
            </p>
        </div>

        <!-- Pengaturan Umum -->
        <div class="mc-notification-setting__section">
            <div class="mc-notification-setting__section-header">
                <SectionIcon type="settings" />
                <span>Notifikasi Aktif</span>
            </div>
            <div class="mc-notification-setting__card">
                <div class="mc-notification-setting__row">
                    <div class="mc-notification-setting__row-content">
                        <div class="mc-notification-setting__row-title">Notifikasi Aktif</div>
                        <div class="mc-notification-setting__row-description">
                            Anda akan menerima notifikasi
                        </div>
                    </div>
                    <div class="mc-notification-setting__row-actions">
                        <b-button variant="outline-secondary" size="sm" class="mc-notification-setting__test-btn"
                            @click="onPlay()">
                            <i class="mc-fa icon-outline_volume-up me-1"></i>
                            Test
                        </b-button>
                        <NotificationToggle v-model="isNotificationEnabled" />
                    </div>
                </div>
            </div>
        </div>

        <!-- Jenis Notifikasi -->
        <div class="mc-notification-setting__section">
            <div class="mc-notification-setting__section-header">
                <SectionIcon type="bell" />
                <span>Jenis Notifikasi</span>
            </div>

            <div class="mc-notification-setting__card">
                <div v-for="type in notificationTypes" :key="type.notification_type_id"
                    class="mc-notification-setting__row">
                    <div class="mc-notification-setting__row-content">
                        <div class="mc-notification-setting__row-title">
                            {{ type.name }}
                        </div>
                    </div>

                    <div class="mc-notification-setting__row-actions">
                        <NotificationToggle :value="selectedNotificationTypes[type.code]"
                            @input="toggleNotification(type.code, $event)" />
                    </div>
                </div>
            </div>
        </div>

        <!-- Footer -->
        <div class="mc-notification-setting__footer">
            <div class="mc-notification-setting__footer-content">
                <div class="mc-notification-setting__footer-info">
                </div>
                <div class="mc-notification-setting__footer-actions">
                    <McButton text="Cancel" variant="secondary" class="mc-notification-setting__btn" @click="cancel" />

                    <McButton text="Save Settings" variant="primary" class="mc-notification-setting__btn"
                        @click="saveSettings" />
                </div>
            </div>
        </div>
    </div>
</template>